// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";

import { mdiClose, mdiContentSavePlus, mdiDelete } from "@mdi/js";
import { Button, Input, Popconfirm, Select, Tooltip } from "antd";
import styled from "styled-components";

import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, DEFAULT_ZOOM } from "models/mapDefaults";

import { useUserDefaults } from "components/user/hooks";

// The map is given a camera rounded to these, but reports its centre back from its own
// projected transform, so reading the centre returns the rounding drift with it. Saving
// that raw would store a longitude the entry then shows rounded. Same constants as the
// jump camera, so the two cannot come apart.
import {
  COORDINATE_DECIMALS,
  ZOOM_DECIMALS,
  round
} from "../utils/resolveJumpCamera";

import { useMapContext } from "../hooks/useMapContext";
import {
  deleteMapLocation,
  getSavedMapLocations,
  saveMapLocation
} from "./api/savedMapLocation";

const { Option } = Select;

const DEFAULT_LOCATION_NAME = "Default Location";
// "System Default" was this entry's previous label. Still reserved so a user cannot
// create a saved location that collides with what the entry used to be called.
const RESERVED_LOCATION_NAMES = [DEFAULT_LOCATION_NAME, "System Default"].map((n) =>
  n.toLocaleLowerCase()
);

export type SavedMapLocationSelectorVariant = "default" | "compact";

interface SavedMapLocationSelectorProps {
  variant?: SavedMapLocationSelectorVariant;
}

export default function SavedMapLocationSelector({
  variant = "default"
}: SavedMapLocationSelectorProps) {
  const map = useMapContext();
  const defaults = useUserDefaults();

  // The "Default Location" entry follows the same precedence as the map itself:
  // the user's own default, else their organization's, else the system one. That
  // resolution is done server-side (EVA-32) and arrives already resolved in
  // `defaults.mapSettings` — do NOT re-derive the precedence here, or this entry
  // and the map can disagree. The DEFAULT_* constants are the pre-load fallback only.
  //
  // `??` throughout, never `||`: latitude 0, longitude 0 and zoom 0 are all legal.
  const defaultLocation = useMemo(
    () => ({
      id: "",
      name: DEFAULT_LOCATION_NAME,
      centerLatitude: defaults?.mapSettings?.latitude ?? DEFAULT_LATITUDE,
      centerLongitude: defaults?.mapSettings?.longitude ?? DEFAULT_LONGITUDE,
      zoomLevel: defaults?.mapSettings?.zoom ?? DEFAULT_ZOOM
    }),
    [
      defaults?.mapSettings?.latitude,
      defaults?.mapSettings?.longitude,
      defaults?.mapSettings?.zoom
    ]
  );

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isAddingLocation, setIsAddingLocation] = useState<boolean>(false);
  const [newLocationName, setNewLocationName] = useState<string>("");

  const [saveError, setSaveError] = useState<string>("");

  const savedLocationQuery = useQuery("savedMapLocations", getSavedMapLocations);

  const saveLocation = useMutation(saveMapLocation, {
    onSuccess: () => {
      setIsAddingLocation(false);
      savedLocationQuery.refetch();
    }
  });

  const onSaveToggle = () => {
    if (isAddingLocation) {
      setNewLocationName("");
      setSaveError("");
    }
    setIsAddingLocation(!isAddingLocation);
  };

  const onSaveLocation = () => {
    if (savedLocationQuery.data?.find((location) => location.name === newLocationName)) {
      setSaveError("Duplicate name detected");
      return;
    } else if (RESERVED_LOCATION_NAMES.includes(newLocationName.toLocaleLowerCase())) {
      setSaveError("Cannot use that name");
      return;
    }
    const center = map.mapbox.getCenter();
    saveLocation.mutate({
      name: newLocationName,
      centerLatitude: round(center.lat, COORDINATE_DECIMALS),
      centerLongitude: round(center.lng, COORDINATE_DECIMALS),
      zoomLevel: round(map.mapbox.getZoom(), ZOOM_DECIMALS)
    });
    setNewLocationName("");
    setSaveError("");
  };

  const deleteLocation = useMutation(deleteMapLocation, {
    onSuccess: (_, deletedId) => {
      if (selectedLocation === deletedId) {
        setSelectedLocation(null);
      }
      savedLocationQuery.refetch();
    }
  });

  const onSelectLocation = (locationId) => {
    let location;
    if (locationId === "") {
      location = defaultLocation;
    } else {
      location = savedLocationQuery.data?.find((location) => location.id === locationId);
    }

    setSelectedLocation(locationId);
    map.mapbox.flyTo({
      center: [location.centerLongitude, location.centerLatitude],
      zoom: location.zoomLevel
    });
  };

  // Memoized because `locationList` is a dependency of the moveend effect below —
  // rebuilding it every render detaches and re-attaches that listener every render.
  const locationList = useMemo(
    () => [
      defaultLocation,
      ...(savedLocationQuery?.data ?? []).map((location) => ({
        id: location?.id,
        name: location.name,
        centerLatitude: location.centerLatitude,
        centerLongitude: location.centerLongitude,
        zoomLevel: location.zoomLevel
      }))
    ],
    [defaultLocation, savedLocationQuery?.data]
  );

  const findPositionDiff = (location, currentCenter, currentZoom) => {
    const latDiff = Math.abs(location.centerLatitude - currentCenter.lat);
    const lngDiff = Math.abs(location.centerLongitude - currentCenter.lng);
    const zoomDiff = Math.abs(location.zoomLevel - currentZoom);
    return { latDiff, lngDiff, zoomDiff };
  };

  useEffect(() => {
    if (!map.mapbox || !selectedLocation) return;

    const startingCenter = map.mapbox.getCenter();
    const startingZoom = map.mapbox.getZoom();
    const startingDifference = findPositionDiff(
      locationList.find((location) => location.id == selectedLocation),
      startingCenter,
      startingZoom
    );

    if (
      startingDifference.latDiff < 0.0001 &&
      startingDifference.lngDiff < 0.0001 &&
      startingDifference.zoomDiff < 0.1
    ) {
      return;
    }

    const onMoveEnd = () => {
      const currentCenter = map.mapbox.getCenter();
      const currentZoom = map.mapbox.getZoom();
      const matchingLocation = locationList.find((location) => {
        const { latDiff, lngDiff, zoomDiff } = findPositionDiff(
          location,
          currentCenter,
          currentZoom
        );
        return (
          latDiff < 0.0001 &&
          lngDiff < 0.0001 &&
          zoomDiff < 0.1 &&
          location.id == selectedLocation
        );
      });
      setSelectedLocation(matchingLocation?.id ?? null);
    };
    map.mapbox.on("moveend", onMoveEnd);
    return () => {
      map.mapbox.off("moveend", onMoveEnd);
    };
  }, [map.mapbox, locationList, selectedLocation]);
  return (
    <>
      <SavedLocationRow>
        <SavedLocationSelect
          data-testid="saved-location-select"
          placeholder="Select Saved Location"
          value={selectedLocation}
          onChange={(value) => {
            onSelectLocation(value);
          }}
          dropdownStyle={{ cursor: "pointer" }}>
          {locationList.map((location) => (
            <Option key={location.id} value={location.id} data-testid={location.name}>
              <Tooltip
                overlayStyle={{ whiteSpace: "pre-line" }}
                placement="left"
                title={`Latitude: ${location.centerLatitude.toFixed(COORDINATE_DECIMALS)}
                                    Longitude: ${location.centerLongitude.toFixed(COORDINATE_DECIMALS)}
                                    Zoom: ${location.zoomLevel.toFixed(ZOOM_DECIMALS)}`}>
                <SavedLocationContainer>
                  <span>{location.name}</span>
                  <Popconfirm
                    placement="right"
                    title={"Are you sure you want to delete this saved map location?"}
                    okText="Delete"
                    okType="danger"
                    onConfirm={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      deleteLocation.mutate(location.id);
                    }}
                    onCancel={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}>
                    {/* Identify the synthetic entry by its id, never by label: the label has
                        already been renamed once, and a user may legitimately own a saved
                        location called "Default Location". */}
                    {location.id !== "" && (
                      <DeleteButton
                        icon={<Icon path={mdiDelete} size={1.1} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      />
                    )}
                  </Popconfirm>
                </SavedLocationContainer>
              </Tooltip>
            </Option>
          ))}
        </SavedLocationSelect>

        {variant !== "compact" && (
          <Tooltip placement="right" title={!isAddingLocation && "Save current location"}>
            <SaveToggle
              data-testid="save-toggle"
              icon={
                <Icon
                  path={isAddingLocation ? mdiClose : mdiContentSavePlus}
                  size={1.1}
                />
              }
              onClick={onSaveToggle}
            />
          </Tooltip>
        )}
      </SavedLocationRow>

      {isAddingLocation && (
        <>
          <Input
            placeholder={"New Saved Location Name"}
            value={newLocationName}
            onChange={(e) => setNewLocationName(e.target.value)}
          />
          <SaveButton onClick={onSaveLocation} disabled={!newLocationName}>
            Save
          </SaveButton>
          <ErrorContainer>{saveError}</ErrorContainer>
        </>
      )}
    </>
  );
}

const SavedLocationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SaveToggle = styled(Button)`
  margin-left: 5px;
  color: #a2aaad;
  display: flex;
  align-items: center;
  justify-content: center;
  &:focus {
    color: #a2aaad;
  }
  &:hover {
    color: black;
  }
`;
const SaveButton = styled(Button)`
  margin-top: 5px;
  background-color: var(--color-primary);
  align-self: flex-end;
`;

const SavedLocationSelect = styled(Select)`
  width: fill-available;

  .ant-select-selector .ant-select-selection-item {
    div button {
      display: none;
    }
  }
`;

const SavedLocationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: calc(100% - 32px);
  }
`;

const DeleteButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  height: 10px;
  color: #a2aaad;
  &:hover {
    background-color: transparent;
    color: red;
  }
  &::after {
    all: unset;
  }
`;

const ErrorContainer = styled.div`
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
`;
